import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LogOut,
  Menu,
  Moon,
  PanelLeft,
  PanelLeftClose,
  Search,
  Sun,
  User,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@cockpit/ui'
import { selectIsSuperAdmin, useAuthStore } from '@/stores/authStore'
import { TenantSwitcher } from '@/components/TenantSwitcher'
import { CommandPalette } from './CommandPalette'
import { navItems, type NavSection } from './nav'

const THEME_KEY = 'cockpit.theme'
const SIDEBAR_KEY = 'cockpit.sidebarCollapsed'
const SIDEBAR_EXPANDED_PX = 220
const SIDEBAR_COLLAPSED_PX = 72

type Theme = 'light' | 'dark'

function readTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1'
  } catch {
    return false
  }
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const toggle = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // ignore
    }
  }, [theme])
  return { theme, toggle }
}

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      return next
    })
  }, [])
  return { collapsed, toggle }
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true,
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const onChange = () => setIsDesktop(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

export function AppShell() {
  const { t } = useTranslation('common')
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const [cmdOpen, setCmdOpen] = useState(false)
  const { collapsed: storedCollapsed, toggle: toggleCollapsed } = useSidebarCollapsed()
  const isDesktop = useIsDesktop()
  const collapsed = storedCollapsed && isDesktop
  const navTitleId = useId()

  const items = useMemo(
    () => navItems.filter((item) => !item.superAdminOnly || isSuperAdmin),
    [isSuperAdmin],
  )

  useEffect(() => {
    setNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!navOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [navOpen])

  const widthPx = isDesktop
    ? collapsed
      ? SIDEBAR_COLLAPSED_PX
      : SIDEBAR_EXPANDED_PX
    : SIDEBAR_EXPANDED_PX

  const shortCaption = (itemKey: string, full: string) =>
    t(`navShort.${itemKey.replace(/^nav\./, '')}`, { defaultValue: full })

  const renderSection = (section: NavSection, labelKey?: string) => {
    const sectionItems = items.filter((i) => i.section === section)
    if (sectionItems.length === 0) return null

    return (
      <div key={section} className={cn('flex w-full flex-col', !collapsed && 'gap-0.5')}>
        {labelKey && !collapsed ? (
          <div className="mx-2.5 mb-1 mt-3 text-[11px] font-medium text-muted-foreground">
            {t(labelKey)}
          </div>
        ) : null}
        {labelKey && collapsed ? (
          <div className="flex w-full items-center justify-center py-2" aria-hidden>
            <i className="block h-px w-5 bg-border not-italic" />
          </div>
        ) : null}

        {sectionItems.map(({ to, labelKey: itemKey, icon: Icon }) => {
          const label = t(itemKey)
          const caption = shortCaption(itemKey, label)
          // IMPORTANT: do NOT pass a className *function* into NavLink when it is
          // wrapped by Radix <TooltipTrigger asChild>. Slot stringifies the fn —
          // rail-cell never applies (measured live: display:block, iconOff:-26).
          const active =
            to === '/desk'
              ? location.pathname === '/desk' || location.pathname === '/'
              : location.pathname === to || location.pathname.startsWith(`${to}/`)

          if (collapsed) {
            return (
              <RailTip key={to} label={label}>
                <NavLink
                  to={to}
                  aria-label={label}
                  className={cn(
                    'rail-cell text-muted-foreground hover:bg-accent hover:text-foreground',
                    active && 'bg-primary/10 text-foreground',
                  )}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  <span className="rail-caption">{caption}</span>
                </NavLink>
              </RailTip>
            )
          }

          return (
            <NavLink
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors duration-150 ease-out-expo',
                active
                  ? 'bg-primary/10 font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              {label}
            </NavLink>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground md:flex-row">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:ring-2 focus:ring-ring"
      >
        {t('a11y.skipToContent')}
      </a>

      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-card px-3 md:hidden">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label={t('a11y.openNav')}
          aria-expanded={navOpen}
          aria-controls="app-sidebar"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Menu className="h-5 w-5" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <TenantSwitcher />
        </div>
      </header>

      {navOpen ? (
        <button
          type="button"
          aria-label={t('a11y.closeNav')}
          className="fixed inset-0 z-40 bg-overlay md:hidden"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <aside
        id="app-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        style={{ width: isDesktop ? widthPx : undefined }}
        className={cn(
          'flex flex-col overflow-hidden border-r border-border bg-muted/55',
          'fixed inset-y-0 left-0 z-50 w-[220px] transition-transform duration-200 ease-out-expo motion-reduce:transition-none',
          navOpen ? 'translate-x-0' : '-translate-x-full',
          'md:static md:z-auto md:translate-x-0 md:transition-[width] md:duration-200 md:ease-out-expo md:motion-reduce:transition-none',
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2 md:hidden">
          <div className="flex flex-col">
            <strong className="font-display text-[15px] font-bold tracking-tight">
              {t('brand.name')}
            </strong>
            <span className="text-[11px] text-muted-foreground">{t('brand.tagline')}</span>
          </div>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label={t('a11y.closeNav')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div
          className={cn(
            'hidden shrink-0 border-b border-border md:flex',
            collapsed
              ? 'h-11 w-full items-center justify-center'
              : 'items-start justify-between gap-2 px-3 py-3',
          )}
        >
          {!collapsed ? (
            <>
              <div className="min-w-0 flex-1">
                <strong className="font-display block text-[15px] font-bold tracking-tight text-foreground">
                  {t('brand.name')}
                </strong>
                <span className="text-[11px] text-muted-foreground">{t('brand.tagline')}</span>
              </div>
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label={t('a11y.collapseSidebar')}
                aria-expanded
                aria-controls="app-sidebar"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <PanelLeftClose className="h-4 w-4" aria-hidden />
              </button>
            </>
          ) : (
            <RailTip label={t('a11y.expandSidebar')}>
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label={t('a11y.expandSidebar')}
                aria-expanded={false}
                aria-controls="app-sidebar"
                className="rail-cell h-11 !py-0 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <PanelLeft className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              </button>
            </RailTip>
          )}
        </div>

        <div
          className={cn(
            'hidden shrink-0 border-b border-border md:block',
            collapsed ? 'w-full' : 'p-2.5',
          )}
        >
          <TenantSwitcher compact={collapsed} />
        </div>

        <nav
          className={cn(
            'rail-stack flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto',
            !collapsed && 'gap-0.5 p-2',
          )}
          aria-labelledby={navTitleId}
        >
          <span id={navTitleId} className="sr-only">
            {t('a11y.mainNav')}
          </span>
          {renderSection('primary')}
          {renderSection('ops', 'nav.sectionOps')}
          {renderSection('org', 'nav.sectionOrg')}
        </nav>

        <div
          className={cn(
            'rail-stack flex w-full shrink-0 flex-col border-t border-border',
            !collapsed && 'gap-0.5 p-2',
          )}
        >
          <RailAction
            collapsed={collapsed}
            label={t('command.open')}
            caption={t('command.short')}
            onClick={() => setCmdOpen(true)}
            icon={Search}
            trailing={
              !collapsed ? (
                <kbd className="ml-auto rounded border border-border px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground">
                  {t('command.shortcut')}
                </kbd>
              ) : null
            }
          />
          <ThemeToggle collapsed={collapsed} />
          <UserMenu collapsed={collapsed} />
        </div>
      </aside>

      <main id="main-content" className="min-w-0 flex-1 overflow-auto" tabIndex={-1}>
        <Outlet />
      </main>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  )
}

function RailTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" align="center" sideOffset={10} className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

function RailAction({
  collapsed,
  label,
  caption,
  onClick,
  icon: Icon,
  trailing,
}: {
  collapsed: boolean
  label: string
  caption?: string
  onClick: () => void
  icon: LucideIcon
  trailing?: ReactNode
}) {
  if (collapsed) {
    return (
      <RailTip label={label}>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="rail-cell text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          <span className="rail-caption">{caption ?? label}</span>
        </button>
      </RailTip>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
      {trailing}
    </button>
  )
}

function ThemeToggle({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('common')
  const { theme, toggle } = useTheme()
  const label = theme === 'dark' ? t('theme.light') : t('theme.dark')
  return (
    <RailAction
      collapsed={collapsed}
      label={label}
      caption={t('theme.short')}
      onClick={toggle}
      icon={theme === 'dark' ? Sun : Moon}
    />
  )
}

function UserMenu({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('common')
  const { t: tAuth } = useTranslation('auth')
  const navigate = useNavigate()
  const admin = useAuthStore((s) => s.admin)
  const logout = useAuthStore((s) => s.logout)

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  if (collapsed) {
    return (
      <DropdownMenu>
        <RailTip label={admin?.email ?? '—'}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={admin?.email ?? '—'}
              className="rail-cell text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <User className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              <span className="rail-caption">{t('a11y.account')}</span>
            </button>
          </DropdownMenuTrigger>
        </RailTip>
        <DropdownMenuContent side="right" align="center" sideOffset={10} className="w-52">
          <UserMenuBody
            email={admin?.email}
            role={admin?.role}
            onLogout={onLogout}
            logoutLabel={tAuth('logout')}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 truncate rounded-md px-2.5 py-1.5 text-left text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card data-[state=open]:bg-accent data-[state=open]:text-foreground"
        >
          <span className="truncate">{admin?.email ?? '—'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-52">
        <UserMenuBody
          email={admin?.email}
          role={admin?.role}
          onLogout={onLogout}
          logoutLabel={tAuth('logout')}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserMenuBody({
  email,
  role,
  onLogout,
  logoutLabel,
}: {
  email?: string | null
  role?: string | null
  onLogout: () => void
  logoutLabel: string
}) {
  return (
    <>
      <DropdownMenuLabel className="flex flex-col gap-1">
        <span className="truncate text-sm">{email}</span>
        {role ? (
          <Badge tone="neutral" className="w-fit">
            {role === 'super_admin' ? 'Super admin' : 'Admin tenant'}
          </Badge>
        ) : null}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onLogout}>
        <LogOut className="mr-2 h-4 w-4" aria-hidden />
        {logoutLabel}
      </DropdownMenuItem>
    </>
  )
}
